import React from "react";
import { DateTime } from "luxon";
import AutoComplete from "../../utils/components/AutoCompleteComp/AutoComplete2.tsx";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import { filterCompte } from "../processing/compteSearcher";
import modes from "../../main/consts/modes.ts";
import type {
  FactureDuplicateCandidate,
  FactureDraft,
  SuperPdpEnvironment,
  SuperPdpInvoiceRecord,
} from "compta-shared";
import LockedBadge from "../../utils/components/LockedBadge.tsx";
import DuplicateFactureWarningModal, { getDuplicateConfirmationKeys } from "../components/DuplicateFactureWarningModal.tsx";

type InvoiceMode = (typeof modes)[number];

type ReceivedInvoiceItem = Pick<
  SuperPdpInvoiceRecord,
  | "_id"
  | "externalId"
  | "createdAt"
  | "statusCode"
  | "statusText"
  | "invoiceNumber"
  | "issueDate"
  | "paymentDueDate"
  | "currencyCode"
  | "sellerName"
  | "buyerName"
  | "totalWithVatCent"
  | "amountDueForPaymentCent"
  | "locked"
> & {
  suggestedCompte: string;
  suggestedMode: InvoiceMode;
  pdfUrl: string | null;
  reviewDraft?: FactureDraft | null;
};

const formatDate = (date: string | null) => {
  if (!date) {
    return "-";
  }
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toFormat("dd/MM/yyyy") : date;
};

const formatAmount = (amountCent: number, currencyCode: string) => {
  return `${(amountCent / 100).toFixed(2)} ${currencyCode}`;
};

const isInvoiceMode = (value: string): value is InvoiceMode => {
  return modes.some((mode) => mode === value);
};

const isSuperPdpEnvironment = (value: string): value is SuperPdpEnvironment => {
  return value === "production" || value === "sandbox";
};

interface ReviewPanelProps {
  selectedInvoice: ReceivedInvoiceItem;
  onClose: () => void;
  onCreated: () => void;
}

export const ElectronicInvoiceReviewPanel = ({
  selectedInvoice: receivedInvoice,
  onClose,
  onCreated,
}: ReviewPanelProps) => {
  const review = receivedInvoice.reviewDraft;
  const reviewDate = review?.dateFacture || review?.datePrelevement;
  const selectedInvoice = {
    ...receivedInvoice,
    ...(review ? {
      sellerName: review.libellé, invoiceNumber: review.modeRef,
      issueDate: reviewDate ? DateTime.fromJSDate(reviewDate).toISODate() || receivedInvoice.issueDate : receivedInvoice.issueDate,
      paymentDueDate: review.paymentDueDate ? DateTime.fromJSDate(review.paymentDueDate).toISODate() : null,
      amountDueForPaymentCent: Math.abs(review.montantCent),
      suggestedMode: review.reviewMode || receivedInvoice.suggestedMode,
      suggestedCompte: review.restoredAccountingData?.compte ?? review.reviewCompte ?? receivedInvoice.suggestedCompte,
    } : {}),
  };
  const utils = trpc.useUtils();
  const isLocked = selectedInvoice.locked === true;
  const [compteSearch, setCompteSearch] = React.useState("");
  const [compte, setCompte] = React.useState(selectedInvoice.suggestedCompte);
  const [modeSearch, setModeSearch] = React.useState("");
  const [mode, setMode] = React.useState<InvoiceMode>(
    selectedInvoice.suggestedMode,
  );
  const [isToPay, setIsToPay] = React.useState(!review);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = React.useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = React.useState<
    FactureDuplicateCandidate[]
  >([]);
  const [duplicateCheckError, setDuplicateCheckError] = React.useState<
    string | null
  >(null);

  const compteCandidates = filterCompte(compteSearch);
  const modeCandidates = modeSearch
    ? modes.filter((candidate) => new RegExp(modeSearch, "i").test(candidate))
    : [];

  const createDraftMutation =
    trpc.superPdp.createDraftFromReceivedInvoice.useMutation({
      onSuccess: async () => {
        await Promise.all([
          utils.superPdp.invalidate(),
          utils.facture.invalidate(),
        ]);
        onCreated();
      },
    });
  const commitMutation = trpc.superPdp.commitReceivedInvoice.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.superPdp.invalidate(),
        utils.facture.invalidate(),
        utils.libellé.invalidate(),
      ]);
      onCreated();
    },
  });
  const rejectMutation = trpc.superPdp.rejectReceivedInvoice.useMutation({
    onSuccess: async () => {
      await utils.superPdp.invalidate();
      onClose();
    },
  });

  const blockingErrors: string[] = [];
  if (!isToPay && !review?.restoredAccountingData && review?.invoiceType !== "resto" && !compte.trim()) {
    blockingErrors.push("Compte comptable obligatoire");
  }
  if (!isToPay && !mode) {
    blockingErrors.push("Mode de règlement obligatoire");
  }

  const commitInvoice = (confirmedDuplicateKeys: string[] = []) => {
    commitMutation.mutate({
      recordId: selectedInvoice._id, compte: compte.trim(), mode, confirmedDuplicateKeys,
    });
  };

  const submitAfterDuplicateCheck = async () => {
    setDuplicateCheckError(null);
    setIsCheckingDuplicates(true);
    try {
      const issueDate = DateTime.fromISO(selectedInvoice.issueDate);
      if (!issueDate.isValid) {
        throw new Error("La date de facture électronique est invalide");
      }
      const duplicates = await utils.facture.checkBusinessDuplicates.fetch({
        modeRef: selectedInvoice.invoiceNumber,
        date: issueDate.toJSDate(),
        datePrelevement: null,
        montantCent: selectedInvoice.amountDueForPaymentCent,
        excludeDraftId: review?._id,
      });
      if (duplicates.length > 0) {
        setDuplicateCandidates(duplicates);
        return;
      }
      if (isToPay) createDraftMutation.mutate({ recordId: selectedInvoice._id, reviewMode: mode, reviewCompte: compte });
      else commitInvoice();
    } catch (error) {
      setDuplicateCheckError(
        error instanceof Error
          ? error.message
          : "Erreur pendant la recherche de doublons",
      );
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  const handleSubmit = () => {
    if (isLocked) {
      return;
    }
    if (blockingErrors.length > 0) {
      return;
    }
    void submitAfterDuplicateCheck();
  };

  const isPending =
    createDraftMutation.isPending ||
    commitMutation.isPending ||
    rejectMutation.isPending ||
    isCheckingDuplicates;

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center gap-3">
        <div>
          <div className="fw-bold">Validation de la facture électronique</div>
          {isLocked ? <LockedBadge /> : null}
          <div className="text-muted small">
            {selectedInvoice.sellerName} • facture{" "}
            {selectedInvoice.invoiceNumber}
          </div>
        </div>
        <div className="d-flex gap-2">
          {selectedInvoice.pdfUrl ? (
            <a
              className="btn btn-outline-secondary btn-sm"
              href={selectedInvoice.pdfUrl}
              target="_blank"
              rel="noreferrer"
            >
              Voir le PDF
            </a>
          ) : null}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
      <div className="card-body">
        <fieldset className="m-0 p-0 border-0" disabled={isLocked}>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Émetteur</label>
              <input
                className="form-control"
                value={selectedInvoice.sellerName}
                readOnly
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Destinataire</label>
              <input
                className="form-control"
                value={selectedInvoice.buyerName}
                readOnly
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Référence facture</label>
              <input
                className="form-control"
                value={selectedInvoice.invoiceNumber}
                readOnly
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Date facture</label>
              <input
                className="form-control"
                value={formatDate(selectedInvoice.issueDate)}
                readOnly
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Échéance</label>
              <input
                className="form-control"
                value={formatDate(selectedInvoice.paymentDueDate)}
                readOnly
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Montant TTC</label>
              <input
                className="form-control"
                value={formatAmount(
                  selectedInvoice.amountDueForPaymentCent,
                  selectedInvoice.currencyCode,
                )}
                readOnly
              />
            </div>
            <div className="col-12">
              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`todo-${selectedInvoice._id}`}
                  checked={isToPay}
                  onChange={(event) => setIsToPay(event.currentTarget.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor={`todo-${selectedInvoice._id}`}
                >
                  À régler
                </label>
              </div>
            </div>
            {!isToPay ? (
              <>
                <div className="col-12 col-md-4">
                  <label className="form-label">Compte comptable</label>
                  <AutoComplete
                    candidates={compteCandidates}
                    onSetSearch={(value) => setCompteSearch(value || "")}
                    onSetValue={(value) => setCompte(value || "")}
                    value={compte}
                    tagsOn={false}
                    acceptNewValues={false}
                    isValid={!!compte.trim()}
                    invalidMessage="Compte obligatoire"
                    isTouched={true}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Mode de règlement</label>
                  <AutoComplete
                    candidates={modeCandidates}
                    onSetSearch={(value) => setModeSearch(value || "")}
                    onSetValue={(value) => {
                      if (isInvoiceMode(value)) {
                        setMode(value);
                      }
                    }}
                    value={mode}
                    tagsOn={false}
                    acceptNewValues={false}
                    isValid={!!mode}
                    invalidMessage="Mode obligatoire"
                    isTouched={true}
                  />
                </div>
              </>
            ) : null}
          </div>

          {createDraftMutation.error ? (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {createDraftMutation.error.message}
            </div>
          ) : null}
          {duplicateCheckError ? (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {duplicateCheckError}
            </div>
          ) : null}
          {commitMutation.error ? (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {commitMutation.error.message}
            </div>
          ) : null}
          {rejectMutation.error ? (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {rejectMutation.error.message}
            </div>
          ) : null}
          {blockingErrors.length > 0 ? (
            <div className="alert alert-warning mt-3 mb-0" role="alert">
              {blockingErrors.join(" • ")}
            </div>
          ) : null}

          <div className="d-flex gap-2 mt-3">
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={isLocked || isPending}
              onClick={() => {
                if (!isLocked) {
                  rejectMutation.mutate(selectedInvoice._id);
                }
              }}
            >
              {rejectMutation.isPending ? "Rejet..." : "Rejeter"}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={
                isLocked || isPending || (!isToPay && blockingErrors.length > 0)
              }
            >
              {isPending
                ? "Création..."
                : isToPay
                  ? "Ajouter aux règlements en attente"
                  : "Créer la facture"}
            </button>
          </div>
        </fieldset>
      </div>
      <DuplicateFactureWarningModal
        show={duplicateCandidates.length > 0}
        items={[
          {
            title: selectedInvoice.invoiceNumber,
            duplicates: duplicateCandidates,
          },
        ]}
        onCancel={() => setDuplicateCandidates([])}
        onConfirm={() => {
          commitInvoice(getDuplicateConfirmationKeys(duplicateCandidates));
          setDuplicateCandidates([]);
        }}
        blocking={isToPay}
        isSubmitting={commitMutation.isPending}
      />
    </div>
  );
};

const ElectronicInvoicesContainer = () => {
  const utils = trpc.useUtils();
  const [environment, setEnvironment] =
    React.useState<SuperPdpEnvironment>("production");
  const { data: connectionStatus } =
    trpc.superPdp.getConnectionStatus.useQuery();
  const isSelectedConnectionConfigured =
    environment === "sandbox" ||
    connectionStatus?.production.configured === true;
  const { data: receivedInvoices, isLoading } =
    trpc.superPdp.getReceivedInvoices.useQuery(environment, {
      enabled: isSelectedConnectionConfigured,
    });
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<
    string | null
  >(null);
  const [syncSummary, setSyncSummary] = React.useState<string | null>(null);
  const [companySummary, setCompanySummary] = React.useState<string | null>(
    null,
  );

  const selectedInvoice =
    receivedInvoices?.find((invoice) => invoice._id === selectedInvoiceId) ||
    null;

  const reviewPanelAnchorRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      node?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [],
  );

  const syncMutation = trpc.superPdp.syncReceivedInvoices.useMutation({
    onSuccess: async (result) => {
      await utils.superPdp.invalidate();
      setSyncSummary(
        `Synchronisation terminée : ${result.insertedCount} nouvelle(s), ${result.updatedCount} mise(s) à jour, ${result.skippedCount} ignorée(s).`,
      );
    },
  });

  const testConnectionMutation = trpc.superPdp.testConnection.useMutation({
    onSuccess: (company) => {
      setCompanySummary(
        `${company.formalName} (${company.env}) • ${company.numberScheme}:${company.number}`,
      );
    },
  });

  if (isLoading) {
    return <div className="p-3">Chargement des factures électroniques...</div>;
  }

  return (
    <div className="container-fluid px-0">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center gap-3">
          <div>
            <div className="fw-bold">Factures électroniques SuperPDP</div>
            <div className="text-muted small">
              Réception manuelle via{" "}
              {environment === "production"
                ? "la production"
                : "Tricatel en bac à sable"}
            </div>
          </div>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={environment}
              onChange={(event) => {
                const selectedEnvironment = event.currentTarget.value;
                if (!isSuperPdpEnvironment(selectedEnvironment)) {
                  return;
                }
                setSelectedInvoiceId(null);
                setEnvironment(selectedEnvironment);
              }}
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox Tricatel</option>
            </select>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => testConnectionMutation.mutate(environment)}
              disabled={
                testConnectionMutation.isPending ||
                !isSelectedConnectionConfigured
              }
            >
              {testConnectionMutation.isPending
                ? "Test..."
                : "Tester la connexion"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => syncMutation.mutate(environment)}
              disabled={
                syncMutation.isPending || !isSelectedConnectionConfigured
              }
            >
              {syncMutation.isPending ? "Synchronisation..." : "Synchroniser"}
            </button>
          </div>
        </div>
        <div className="card-body">
          {!isSelectedConnectionConfigured ? (
            <div className="alert alert-warning" role="alert">
              Configurez les identifiants Super-PDP de production dans les
              paramètres avant de synchroniser.
            </div>
          ) : null}
          {companySummary ? (
            <div className="alert alert-success" role="alert">
              Connexion SuperPDP valide : {companySummary}
            </div>
          ) : null}
          {testConnectionMutation.error ? (
            <div className="alert alert-danger" role="alert">
              {testConnectionMutation.error.message}
            </div>
          ) : null}
          {syncSummary ? (
            <div className="alert alert-info" role="alert">
              {syncSummary}
            </div>
          ) : null}
          {syncMutation.error ? (
            <div className="alert alert-danger" role="alert">
              {syncMutation.error.message}
            </div>
          ) : null}

          {!receivedInvoices || receivedInvoices.length === 0 ? (
            <div className="text-muted">
              Aucune facture électronique en attente.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Émetteur</th>
                    <th>Référence</th>
                    <th>Date facture</th>
                    <th>Échéance</th>
                    <th>Montant TTC</th>
                    <th>Statut PDP</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receivedInvoices.map((invoice) => (
                    <tr
                      key={invoice._id}
                      className={
                        selectedInvoiceId === invoice._id ? "table-primary" : ""
                      }
                    >
                      <td>{invoice.sellerName}</td>
                      <td>{invoice.invoiceNumber}</td>
                      <td>{formatDate(invoice.issueDate)}</td>
                      <td>{formatDate(invoice.paymentDueDate)}</td>
                      <td>
                        {formatAmount(
                          invoice.amountDueForPaymentCent,
                          invoice.currencyCode,
                        )}
                      </td>
                      <td>
                        {invoice.statusText || invoice.statusCode || "-"}
                        {invoice.locked === true ? <LockedBadge /> : null}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          {invoice.pdfUrl ? (
                            <a
                              className="btn btn-outline-secondary btn-sm"
                              href={invoice.pdfUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Voir PDF
                            </a>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={invoice.locked === true}
                            onClick={() => {
                              if (invoice.locked !== true) {
                                setSelectedInvoiceId(invoice._id);
                              }
                            }}
                          >
                            Traiter
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedInvoice ? (
        <div key={selectedInvoice._id} ref={reviewPanelAnchorRef}>
          <ElectronicInvoiceReviewPanel
            selectedInvoice={selectedInvoice}
            onClose={() => setSelectedInvoiceId(null)}
            onCreated={() => setSelectedInvoiceId(null)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ElectronicInvoicesContainer;
